import { computed } from 'vue';

import { CORPORATE_USER_ROLES, DEFAULT_CORPORATE_USER_ROLE } from '@/constants/corporateUserRole.js';
import store from '@/store/Index.js';

const STUDENT_ROLE_NAME = 'Student';

/**
 * Resolve the current user's corporate sub-view ("admin" or "student") from
 * the Vuex User store.
 *
 * Decision rule (locked in with product):
 *   - STUDENT iff hasRole('Student') AND NOT hasAdminRights
 *   - ADMIN  otherwise
 *
 * "hasAdminRights" wins: a user with both Student and any admin-level role
 * (Owner / Admin / Instruction Admin) sees the admin view. A user with
 * Student plus a non-admin staff role (e.g. Instructor) still sees the
 * student view - product expectation is that ONLY true admins see the
 * corporate admin surface.
 *
 * This is a CORPORATE-only concern. The function returns 'admin' for any
 * client variant other than corporate as well, because the role resolver wrappers
 * are only mounted inside the corporate branch of the client resolver; the
 * value is undefined outside that context.
 *
 * Failure mode: when the Vuex User module isn't registered (most likely in
 * unit tests), we fall back to `DEFAULT_CORPORATE_USER_ROLE` (STUDENT) to
 * fail closed.
 *
 * @returns {'admin' | 'student'}
 */
export function getCurrentCorporateUserRole() {
    const hasRoleGetter = store.getters['User/hasRole'];

    if (typeof hasRoleGetter !== 'function') {
        return DEFAULT_CORPORATE_USER_ROLE;
    }

    const hasStudentRole = Boolean(hasRoleGetter(STUDENT_ROLE_NAME));
    const hasAdminRights = Boolean(store.getters['User/hasAdminRights']);

    if (hasStudentRole && !hasAdminRights) {
        return CORPORATE_USER_ROLES.STUDENT;
    }

    return CORPORATE_USER_ROLES.ADMIN;
}

/**
 * @returns {boolean} True when the current user should see the corporate admin view.
 */
export function isCorporateAdminUser() {
    return getCurrentCorporateUserRole() === CORPORATE_USER_ROLES.ADMIN;
}

/**
 * @returns {boolean} True when the current user should see the corporate student view.
 */
export function isCorporateStudentUser() {
    return getCurrentCorporateUserRole() === CORPORATE_USER_ROLES.STUDENT;
}

/**
 * Composable form of {@link getCurrentCorporateUserRole}. Use inside Vue
 * components so the returned value stays reactive when the underlying Vuex
 * user state changes (e.g. role assignment, impersonation, logout).
 *
 * For router middleware or other non-component code, prefer the plain
 * {@link isCorporateAdminUser} / {@link isCorporateStudentUser} helpers.
 *
 * @returns {{
 *   role: import('vue').ComputedRef<'admin' | 'student'>,
 *   isAdmin: import('vue').ComputedRef<boolean>,
 *   isStudent: import('vue').ComputedRef<boolean>,
 * }}
 */
export function useCorporateUserRole() {
    const role = computed(() => getCurrentCorporateUserRole());

    return {
        role,
        isAdmin: computed(() => role.value === CORPORATE_USER_ROLES.ADMIN),
        isStudent: computed(() => role.value === CORPORATE_USER_ROLES.STUDENT),
    };
}

/**
 * Resolve a per-role component (or any value) from a map keyed by corporate
 * user role identifier. Sibling to `resolveClientComponent` from
 * `useEdverumClient.js`.
 *
 * Resolution rules:
 *   1. If `map[currentRole]` exists, return it.
 *   2. Else, log console.error describing the gap and fall back to the
 *      `DEFAULT_CORPORATE_USER_ROLE` entry so the UI degrades to the safe
 *      default instead of crashing in production.
 *   3. If even the default key isn't present, throw - that's an unambiguous
 *      developer error and should surface immediately.
 *
 * Wrapper components are expected to register entries for BOTH 'admin' and
 * 'student' keys; the fallback exists to keep the runtime resilient if a
 * future role variant is added (e.g. a manager-only view) before every
 * wrapper has been updated.
 *
 * @template T
 * @param {Record<string, T>} map
 * @returns {T}
 */
export function resolveCorporateView(map) {
    const role = getCurrentCorporateUserRole();

    if (Object.hasOwn(map, role)) {
        return map[role];
    }

    if (Object.hasOwn(map, DEFAULT_CORPORATE_USER_ROLE)) {
        console.error(
            `[useCorporateUserRole] No entry registered for corporate user role "${role}". ` +
                `Registered keys: [${Object.keys(map).join(', ')}]. ` +
                `Falling back to "${DEFAULT_CORPORATE_USER_ROLE}".`
        );
        return map[DEFAULT_CORPORATE_USER_ROLE];
    }

    throw new Error(
        `[useCorporateUserRole] No entry registered for corporate user role "${role}" ` +
            `and no fallback under "${DEFAULT_CORPORATE_USER_ROLE}". ` +
            `Registered keys: [${Object.keys(map).join(', ')}].`
    );
}
